import { useState, useEffect } from "react";
import { getHoursCloseTicketsAuto } from "../../config";
import toastError from "../../errors/toastError";

import api from "../../services/api";

const useTickets = ({
    searchParam,
    pageNumber,
    status,
    date,
    showAll,
    showOrphans,
    queueIds,
    withUnreadMessages,
    hideChannels,
    contactStateId,
    tagIds,
    contactTypeIds,
    filterUserIds,
    includeUnassigned,
    queueAssignment,
    sortOrder,
    onlyUnanswered,
    deleted,
    // feat_346 — entero que bumpea TicketsList al despertar. NO viaja en el
    // request: está solo para re-disparar el GET. `setPageNumber(1)` del RESET
    // no alcanza cuando la página ya es 1 (misma dep, mismo valor → el effect
    // no vuelve a correr y la lista se quedaría vacía tras el RESET).
    resyncKey,
}) => {
    const [loading, setLoading] = useState(true);
    const [hasMore, setHasMore] = useState(false);
    const [tickets, setTickets] = useState([]);
    const [count, setCount] = useState(0);
    const [contactsWithoutTicket, setContactsWithoutTicket] = useState([]);

    // feat_323 — `status` puede ser array (bandeja unificada). Un array es una
    // referencia NUEVA en cada render: como dep del useEffect dispararía el
    // fetch en loop cada 500 ms. Se serializa a primitiva estable, igual que
    // queueIds/filterUserIds (precedente feat_301). El `sort()` es sobre una
    // copia para no mutar el array del caller.
    // feat_325 — `queueAssignment` viaja en la MISMA clave: es un escalar, pero
    // tiene que estar en las deps o el fetch no se rehace al cambiar de bucket.
    // feat_326 — `sortOrder` y `onlyUnanswered` entran a la MISMA clave por lo
    // mismo: sin ellos el fetch no se rehace al cambiar de orden o al prender el
    // filtro, y el guard del socket de TicketsList (que también depende de
    // filterKey) se quedaría con el valor viejo.
    const filterKey = [
        Array.isArray(status) ? status.slice().sort().join(",") : (status || ""),
        queueAssignment || "any",
        sortOrder || "chrono",
        onlyUnanswered ? "unanswered" : "all"
    ].join("|");

    useEffect(() => {
        setLoading(true);
        const delayDebounceFn = setTimeout(() => {
            const fetchTickets = async() => {
                try {
                    const { data } = await api.get("/tickets", {
                        params: {
                            searchParam,
                            pageNumber,
                            status,
                            date,
                            showAll,
                            showOrphans,
                            queueIds,
                            withUnreadMessages,
                            hideChannels,
                            contactStateId,
                            tagIds: JSON.stringify(tagIds || []),
                            contactTypeIds: JSON.stringify(contactTypeIds || []),
                            // feat_301: llega YA serializado desde TicketsList
                            // (evita array nuevo por render en las deps).
                            filterUserIds,
                            includeUnassigned,
                            queueAssignment,
                            sortOrder,
                            // undefined → axios no lo manda → el controller lo
                            // dropea → sin filtro. `false` viajaría como
                            // "false", que la allowlist descarta igual, pero
                            // así el request queda idéntico al histórico.
                            onlyUnanswered: onlyUnanswered || undefined,
                            deleted,
                        },
                    })
                    setTickets(data.tickets)
                    setContactsWithoutTicket(data.contactsWithoutTicket || [])

                    let horasFecharAutomaticamente = getHoursCloseTicketsAuto();

                    // feat_323 — el autoclose solo corre para la lista que es
                    // EXCLUSIVAMENTE de abiertos. En modo unificado el array trae
                    // también pending: cerrar esos por inactividad sería un cambio
                    // de comportamiento que este feature no pide.
                    const isOpenOnly = status === "open" ||
                        (Array.isArray(status) && status.length === 1 && status[0] === "open");

                    if (isOpenOnly && horasFecharAutomaticamente && horasFecharAutomaticamente !== "" &&
                        horasFecharAutomaticamente !== "0" && Number(horasFecharAutomaticamente) > 0) {

                        let dataLimite = new Date()
                        dataLimite.setHours(dataLimite.getHours() - Number(horasFecharAutomaticamente))

                        data.tickets.forEach(ticket => {
                            if (ticket.status !== "closed") {
                                let dataUltimaInteracaoChamado = new Date(ticket.updatedAt)
                                if (dataUltimaInteracaoChamado < dataLimite)
                                    closeTicket(ticket)
                            }
                        })
                    }

                    setHasMore(data.hasMore)
                    setCount(data.count)
                    setLoading(false)
                } catch (err) {
                    setLoading(false)
                    toastError(err)
                }
            }

            const closeTicket = async(ticket) => {
                await api.put(`/tickets/${ticket.id}`, {
                    status: "closed",
                    userId: ticket.userId || null,
                })
            }

            fetchTickets()
        }, 500)
        return () => clearTimeout(delayDebounceFn)
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        searchParam,
        pageNumber,
        // feat_323/325/326: filterKey, NO status/queueAssignment/sortOrder/
        // onlyUnanswered sueltos — ver el comentario de la serialización.
        filterKey,
        date,
        showAll,
        showOrphans,
        queueIds,
        withUnreadMessages,
        hideChannels,
        contactStateId,
        tagIds,
        contactTypeIds,
        filterUserIds,
        includeUnassigned,
        deleted,
        resyncKey,
    ])

    return { tickets, loading, hasMore, count, contactsWithoutTicket };
};

export default useTickets;